import React, { useEffect, useRef, useState } from 'react';
import { AlertIcon, CheckIcon, CloseIcon } from './icons';

// In-app replacements for alert(), confirm() and prompt(), which render as browser chrome and block the page.
// Both work from anywhere (event-driven), so callers need no provider or hook.

export type ToastTone = 'error' | 'warning' | 'success' | 'info';

interface ToastRequest {
  message: string;
  tone: ToastTone;
}

interface ConfirmRequest {
  message: string;
  confirmLabel: string;
  danger: boolean;
  resolve: (confirmed: boolean) => void;
}

interface PromptRequest {
  message: string;
  confirmLabel: string;
  defaultValue: string;
  placeholder: string;
  maxLength: number;
  resolve: (value: string | null) => void;
}

const TOAST_EVENT = 'cv:toast';
const CONFIRM_EVENT = 'cv:confirm';
const PROMPT_EVENT = 'cv:prompt';
const TOAST_MS = 6000;

export const notify = (message: string, tone: ToastTone = 'error') =>
  window.dispatchEvent(new CustomEvent<ToastRequest>(TOAST_EVENT, { detail: { message, tone } }));

export const confirmDialog = (
  message: string,
  { confirmLabel = 'Confirm', danger = false }: { confirmLabel?: string; danger?: boolean } = {},
): Promise<boolean> => new Promise((resolve) => {
  window.dispatchEvent(new CustomEvent<ConfirmRequest>(CONFIRM_EVENT, {
    detail: { message, confirmLabel, danger, resolve },
  }));
});

// Resolves to the trimmed text, or null when cancelled.
export const promptDialog = (
  message: string,
  {
    confirmLabel = 'Save', defaultValue = '', placeholder = '', maxLength = 100,
  }: { confirmLabel?: string; defaultValue?: string; placeholder?: string; maxLength?: number } = {},
): Promise<string | null> => new Promise((resolve) => {
  window.dispatchEvent(new CustomEvent<PromptRequest>(PROMPT_EVENT, {
    detail: { message, confirmLabel, defaultValue, placeholder, maxLength, resolve },
  }));
});

const PromptBox: React.FC<{ request: PromptRequest; onAnswer: (value: string | null) => void }> = ({ request, onAnswer }) => {
  const [value, setValue] = useState(request.defaultValue);
  const trimmed = value.trim();

  return (
    <div className="cv-modal-backdrop" onClick={() => onAnswer(null)}>
      <form
        className="cv-modal cv-modal--confirm"
        role="dialog"
        aria-modal="true"
        aria-label={request.message}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => { if (e.key === 'Escape') onAnswer(null); }}
        onSubmit={(e) => { e.preventDefault(); if (trimmed) onAnswer(trimmed); }}
      >
        <label className="cv-confirm__message" htmlFor="cv-prompt-input">{request.message}</label>
        <input
          id="cv-prompt-input"
          className="cv-input"
          value={value}
          placeholder={request.placeholder}
          maxLength={request.maxLength}
          autoFocus
          onFocus={(e) => e.currentTarget.select()}
          onChange={(e) => setValue(e.target.value)}
        />
        <div className="cv-confirm__actions">
          <button type="button" className="cv-tool-btn" onClick={() => onAnswer(null)}>Cancel</button>
          <button type="submit" className="cv-tool-btn cv-tool-btn--primary" disabled={!trimmed}>
            {request.confirmLabel}
          </button>
        </div>
      </form>
    </div>
  );
};

interface Toast extends ToastRequest {
  id: number;
}

const Notifications: React.FC = () => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [pendingConfirm, setPendingConfirm] = useState<ConfirmRequest | null>(null);
  const [pendingPrompt, setPendingPrompt] = useState<PromptRequest | null>(null);
  const nextId = useRef(0);
  const confirmButtonRef = useRef<HTMLButtonElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  const dismiss = (id: number) => setToasts((all) => all.filter((t) => t.id !== id));

  useEffect(() => {
    const onToast = (e: Event) => {
      const { message, tone } = (e as CustomEvent<ToastRequest>).detail;
      const id = nextId.current++;
      // A message already on screen (e.g. several rejected files) is not stacked again.
      setToasts((all) => (all.some((t) => t.message === message) ? all : [...all, { id, message, tone }]));
      setTimeout(() => dismiss(id), TOAST_MS);
    };
    const onConfirm = (e: Event) => setPendingConfirm((e as CustomEvent<ConfirmRequest>).detail);
    const onPrompt = (e: Event) => setPendingPrompt((e as CustomEvent<PromptRequest>).detail);

    window.addEventListener(TOAST_EVENT, onToast);
    window.addEventListener(CONFIRM_EVENT, onConfirm);
    window.addEventListener(PROMPT_EVENT, onPrompt);

    return () => {
      window.removeEventListener(TOAST_EVENT, onToast);
      window.removeEventListener(CONFIRM_EVENT, onConfirm);
      window.removeEventListener(PROMPT_EVENT, onPrompt);
    };
  }, []);

  useEffect(() => {
    // A destructive action must not be one stray Enter away.
    if (pendingConfirm) (pendingConfirm.danger ? cancelButtonRef : confirmButtonRef).current?.focus();
  }, [pendingConfirm]);

  const answer = (confirmed: boolean) => {
    pendingConfirm?.resolve(confirmed);
    setPendingConfirm(null);
  };

  return (
    <>
      {toasts.length > 0 && (
        <div className="cv-toasts">
          {toasts.map((toast) => (
            <div
              key={toast.id}
              className={`cv-toast cv-toast--${toast.tone}`}
              role={toast.tone === 'error' ? 'alert' : 'status'}
            >
              <span className="cv-toast__icon">
                {toast.tone === 'success' ? <CheckIcon size={16} /> : <AlertIcon size={16} />}
              </span>
              <span className="cv-toast__message">{toast.message}</span>
              <button type="button" className="cv-icon-btn cv-toast__close" onClick={() => dismiss(toast.id)} aria-label="Dismiss">
                <CloseIcon size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {pendingPrompt && (
        <PromptBox
          key={pendingPrompt.message + pendingPrompt.defaultValue}
          request={pendingPrompt}
          onAnswer={(value) => { pendingPrompt.resolve(value); setPendingPrompt(null); }}
        />
      )}

      {pendingConfirm && (
        <div className="cv-modal-backdrop" onClick={() => answer(false)}>
          <div
            className="cv-modal cv-modal--confirm"
            role="alertdialog"
            aria-modal="true"
            aria-label={pendingConfirm.message}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => { if (e.key === 'Escape') answer(false); }}
          >
            <p className="cv-confirm__message">{pendingConfirm.message}</p>
            <div className="cv-confirm__actions">
              <button ref={cancelButtonRef} type="button" className="cv-tool-btn" onClick={() => answer(false)}>Cancel</button>
              <button
                ref={confirmButtonRef}
                type="button"
                className={`cv-tool-btn ${pendingConfirm.danger ? 'cv-tool-btn--danger' : 'cv-tool-btn--primary'}`}
                onClick={() => answer(true)}
              >
                {pendingConfirm.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Notifications;
